import type { Room, Vec2 } from "../types";
import { editableWalls, wallLabel, type WallSegment } from "../interiorWalls";

/**
 * Wall keys name one wall face for per-wall finishes. Same format as the 3D
 * shell's `WallPlan.key`, so a saved override is a direct lookup there.
 */
export type WallHost = { wallIndex: number; interiorWallId?: string; interiorSegment?: number };

export function outlineWallKey(index: number): string {
  return `outline:${index}`;
}

export function interiorWallKey(wallId: string, segment: number): string {
  return `interior:${wallId}:${segment}`;
}

export function wallKeyFor(host: WallHost): string {
  return host.interiorWallId != null && host.interiorSegment != null
    ? interiorWallKey(host.interiorWallId, host.interiorSegment)
    : outlineWallKey(host.wallIndex);
}

export function segmentKey(seg: WallSegment): string {
  return seg.kind === "interior" && seg.wallId != null && seg.segment != null
    ? interiorWallKey(seg.wallId, seg.segment)
    : outlineWallKey(seg.boundaryIndex ?? seg.labelIndex);
}

/** The letter the Walls view shows for a wall ("B"), or null if it no longer exists. */
export function wallKeyLetter(room: Room, key: string): string | null {
  const seg = editableWalls(room).find((s) => segmentKey(s) === key);
  return seg ? wallLabel(seg.labelIndex) : null;
}

interface Edge {
  key: string;
  a: string;
  b: string;
  pa: Vec2;
  pb: Vec2;
}

function edges(room: Room): Edge[] {
  if (room.points.length < 3) return [];
  return editableWalls(room).map((s) => ({ key: segmentKey(s), a: s.aId, b: s.bId, pa: s.a, pb: s.b }));
}

const near = (p: Vec2, q: Vec2, tol = 0.5) => Math.hypot(p.x - q.x, p.y - q.y) <= tol;

/** Length of `e` that lies along `f` (collinear within an inch), or 0. */
function collinearOverlap(e: Edge, f: Edge): number {
  const fx = f.pb.x - f.pa.x;
  const fy = f.pb.y - f.pa.y;
  const flen = Math.hypot(fx, fy);
  const elen = Math.hypot(e.pb.x - e.pa.x, e.pb.y - e.pa.y);
  if (flen < 1e-6 || elen < 1e-6) return 0;
  const ux = fx / flen;
  const uy = fy / flen;
  const perp = (p: Vec2) => Math.abs((p.x - f.pa.x) * uy - (p.y - f.pa.y) * ux);
  if (perp(e.pa) > 1 || perp(e.pb) > 1) return 0;
  const t = (p: Vec2) => (p.x - f.pa.x) * ux + (p.y - f.pa.y) * uy;
  const t0 = Math.min(t(e.pa), t(e.pb));
  const t1 = Math.max(t(e.pa), t(e.pb));
  return Math.max(0, Math.min(t1, flen) - Math.max(t0, 0));
}

function sameTopology(prev: Room, next: Room): boolean {
  const shape = (r: Room) =>
    JSON.stringify([r.pointIds ?? r.points.length, (r.interiorWalls ?? []).map((w) => [w.id, w.vertexIds])]);
  return shape(prev) === shape(next);
}

/**
 * Carry per-wall finishes across a room edit. Keys are positional
 * (`outline:3`), so a corner added or removed would otherwise shift every
 * override onto its neighbor. A wall keeps its override when it's the same
 * wall (same corners, same place), when it's part of a wall that was split
 * or moved along its own line, or when it's what's left of two walls
 * merged by removing the corner between them. Anything else is dropped.
 */
export function remapWallFinishes(prev: Room, next: Room): Room {
  const overrides = prev.finishes?.wallOverrides;
  if (!overrides || Object.keys(overrides).length === 0) return next;
  if (sameTopology(prev, next)) {
    if (next.finishes?.wallOverrides === overrides) return next;
    return { ...next, finishes: { ...next.finishes, wallOverrides: overrides } };
  }
  const before = edges(prev);
  const after = edges(next);
  const nextIds = new Set(after.flatMap((e) => [e.a, e.b]));
  const prevPos = new Map<string, Vec2>();
  for (const e of before) {
    prevPos.set(e.a, e.pa);
    prevPos.set(e.b, e.pb);
  }
  const stayed = (id: string, p: Vec2) => {
    const q = prevPos.get(id);
    return !!q && near(p, q);
  };
  const out: Record<string, (typeof overrides)[string]> = {};
  for (const e of after) {
    const exact = before.find(
      (f) => ((f.a === e.a && f.b === e.b) || (f.a === e.b && f.b === e.a)) && stayed(e.a, e.pa) && stayed(e.b, e.pb)
    );
    let source: Edge | undefined = exact;
    if (!source) {
      let best = 0;
      for (const f of before) {
        if (!overrides[f.key]) continue;
        const o = collinearOverlap(e, f);
        if (o > best) {
          best = o;
          source = f;
        }
      }
      const len = Math.hypot(e.pb.x - e.pa.x, e.pb.y - e.pa.y);
      if (best < len * 0.5) source = undefined;
    }
    if (!source && stayed(e.a, e.pa) && stayed(e.b, e.pb)) {
      // Two walls merged: the corner between them is gone.
      const halves = before.filter(
        (f) => (f.a === e.a || f.b === e.a || f.a === e.b || f.b === e.b) && !nextIds.has(f.a === e.a || f.a === e.b ? f.b : f.a)
      );
      source = halves.find((f) => overrides[f.key]);
    }
    const finish = source ? overrides[source.key] : undefined;
    if (finish) out[e.key] = finish;
  }
  const finishes = { ...next.finishes };
  if (Object.keys(out).length > 0) finishes.wallOverrides = out;
  else delete finishes.wallOverrides;
  const room: Room = { ...next, finishes };
  if (Object.keys(finishes).length === 0) delete room.finishes;
  return room;
}
